import React from 'react'
import { SlideNotice } from './SlideNotice'
import { registerSlide } from './registry'
import { SlotScrCards } from '../slots'
import { StageLayout } from '../layouts/StageLayout'
import { KpiTile, kpiFigureTileValue, kpiLocale } from '../components/data-display/KpiTile/KpiTile'
import { readExecSummaryKpiStrip } from '../plots/kpi/readAdapters'
import { schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import type { EditableSlideProps } from './contracts'
import type { ExecSummaryKpi, ExecSummarySlideData } from './renderer-internal-types'

const EXEC_SUMMARY = 'exec-summary' as const

function plotLowBaseLabel(plot: unknown): string | undefined {
  if (!plot || typeof plot !== 'object') {
    return undefined
  }
  const data = (plot as { data?: unknown }).data
  if (!data || typeof data !== 'object') {
    return undefined
  }
  const labels = (data as { labels?: unknown }).labels
  if (!labels || typeof labels !== 'object') {
    return undefined
  }
  const note = (labels as { plot_low_base?: unknown }).plot_low_base
  return typeof note === 'string' && note.trim() ? note : undefined
}

export const ExecSummarySlide = registerSlide(
  'exec-summary',
  function ExecSummarySlide(props: ExecSummarySlideData & EditableSlideProps) {
    const { columns: rawColumns, kpis, eyebrow, clientLogoUrl, footerText, actionTitle, actionSubtitle, editable, onEdit } = props
    // Absent means a deck stored before the switch: it still draws its subtitle.
    const showActionSubtitle = props.showActionSubtitle !== false
    const columns = rawColumns ?? []
    const showNewKpiStrip = Array.isArray(kpis) && kpis.length > 0
    const hasRetiredMetrics = kpis == null && columns.some((col) => Boolean(col.anchor))
    const deckLocale = kpiLocale(props)
    // The strip's stamped kpi plot (plots wave 7, D4): each tile prints its
    // item's figure through the kpi plot's one formatter. A slide stored before
    // the stamp reads each tile through `kpiTileValue` (its kpi adapter or the
    // legacy `formatKpiValue`).
    const stampedKpis = showNewKpiStrip ? readExecSummaryKpiStrip(props.plot, (kpis as Array<ExecSummaryKpi>).length) : null
    const lowBaseLabel = plotLowBaseLabel(props.plot)
    const kpiSentenceClamp = schemaBackedProseClampProps('kpiSentence', EXEC_SUMMARY, {
      lineHeight: 'var(--ds-lh-snug)',
    })

    return (
      <StageLayout
        preset="exec-summary"
        eyebrowText={eyebrow}
        clientLogoUrl={clientLogoUrl}
        footerText={footerText}
        actionTitle={actionTitle ?? undefined}
        actionSubtitle={showActionSubtitle ? (actionSubtitle ?? undefined) : undefined}
        headlineMaxLines={showActionSubtitle ? undefined : { title: 2 }}
        eyebrowTextTransform="none"
        editable={editable}
        onEdit={onEdit}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            gap: 'var(--ds-exec-summary-section-gap)',
          }}
        >
          <SlotScrCards columns={columns} editable={editable} onEdit={onEdit} renderMode={props.renderMode} />

          {showNewKpiStrip ? (
            <>
              {/* New exec-summary KPI strip (Figma layout_ZBHZQE, gap 24). The value sits
                  on its own line with the copy stacked tightly under it, both the full
                  tile width, so the number's digit count cannot shift the layout. Tiles
                  are top-aligned: every value sits on the same line whatever its copy
                  wraps to, and a tile whose copy runs to 3 lines grows downward into the
                  strip's own slack rather than pushing the value line around. */}
              <div
                style={{
                  flexShrink: 0,
                  borderTop: 'var(--ds-border-thin) solid var(--ds-border-color)',
                }}
              />
              <div
                data-testid="exec-summary-kpi-strip"
                style={{
                  flexShrink: 0,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${(kpis as Array<ExecSummaryKpi>).length}, minmax(0, 1fr))`,
                  gap: 'var(--ds-gap-xl)',
                  alignItems: 'start',
                }}
              >
                {(kpis as Array<ExecSummaryKpi>).map((kpi, idx) => (
                  <KpiTile
                    key={idx}
                    kpi={kpi}
                    clamp={kpiSentenceClamp}
                    locale={deckLocale}
                    testId={`exec-summary-kpi-${idx}`}
                    valueTestId={`exec-summary-kpi-value-${idx}`}
                    copyTestId={`exec-summary-kpi-copy-${idx}`}
                    sentenceTestId={`exec-summary-kpi-sentence-${idx}`}
                    valueColor="var(--ds-exec-kpi)"
                    formattedValue={stampedKpis ? kpiFigureTileValue(stampedKpis[idx]?.figure, deckLocale) ?? undefined : undefined}
                    lowBaseNote={stampedKpis?.[idx]?.lowBase ? lowBaseLabel : undefined}
                  />
                ))}
              </div>
            </>
          ) : hasRetiredMetrics && props.displayLabels?.retiredMetrics ? (
            <SlideNotice>{props.displayLabels.retiredMetrics}</SlideNotice>
          ) : null}
        </div>
      </StageLayout>
    )
  },
)
